/**
 * MyAccess Component (ORCH-336; PRD User Manager UM-14)
 *
 * Purpose: A person's own access, read-only: their Company, roles and
 *          security attributes in each tenant they belong to.
 * Logic: Value names are resolved in the active tenant, which knows the shared
 *        GLOBAL values; another tenant's own local values show as "a value in
 *        that tenant" rather than as an id.
 * Dependencies: userManagementService.getOwnMemberships, useMembershipFields
 */

import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, Paper, Typography } from '@mui/material';
import userManagementService, { userErrorMessage } from '../../services/userManagementService';
import { Member } from '../../types/userManagement';
import { asList } from '../../utils/listValues';
import { COMPANY_FIELD } from './membership';
import { useMembershipFields } from './useMembershipFields';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MyAccess: React.FC<{ activeTenantId: string }> = ({ activeTenantId }) => {
  const [memberships, setMemberships] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { fieldLabel, valueName } = useMembershipFields(activeTenantId);

  useEffect(() => {
    let current = true;
    userManagementService
      .getOwnMemberships()
      .then((data) => current && setMemberships(data))
      .catch((e) => current && setError(userErrorMessage(e, 'Your access could not be loaded.')));
    return () => {
      current = false;
    };
  }, [activeTenantId]);

  const named = (fielddefId: string, valueId: string) => {
    const name = valueName(fielddefId, valueId);
    return UUID.test(name) ? 'a value in that tenant' : name;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="my-access">
      <Typography variant="h6">My Access</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What you hold in each tenant. To change it, ask a user admin in that tenant.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 1.5,
        }}
      >
        {(memberships || []).map((m) => {
          const company = asList(m.metadata[COMPANY_FIELD])[0];
          const attributes = Object.entries(m.metadata)
            .filter(([fielddefId]) => fielddefId !== COMPANY_FIELD)
            .map(([fielddefId, value]) => {
              const values = asList(value).map((id) => named(fielddefId, id));
              return values.length ? `${fieldLabel(fielddefId)}: ${values.join(', ')}` : null;
            })
            .filter(Boolean);
          return (
            <Paper
              key={m.tenant_id}
              variant="outlined"
              sx={{ p: 1.5 }}
              data-testid={`my-access-${m.tenant_id}`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">{m.tenant_name || m.tenant_id}</Typography>
                {m.tenant_id === activeTenantId && (
                  <Chip size="small" variant="outlined" label="Current" />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {company ? named(COMPANY_FIELD, company) : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {m.roles.map((role) => (
                  <Chip key={role.role_id} size="small" label={role.name} />
                ))}
                {m.roles.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No roles
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {attributes.length ? attributes.join(' · ') : 'No security attributes'}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

export default MyAccess;
